<template>
  <BCard>
    <!-- Header Section -->
    <div class="mb-2 flex flex-row justify-between items-center">
      <div class="text-black font-semibold text-xl">
        Atur Biaya Berlangganan
      </div>
    </div>

    <!-- Subscription Overview Section -->
    <BCard class="!bg-[#F8F8F8] border border-[#E5E5E5] !rounded-[10px]">
      <div class="card border col-12 subscription-overview px-0 py-0 m-0">
        <div
          class="d-flex justify-content-between flex-md-row flex-column-reverse px-2 py-2"
        >
          <div class="d-flex flex-column justify-content-start gap-2 w-100">
            <div class="d-flex justify-content-between">
              <div class="d-flex gap-3 align-items-center">
                <div class="bg-[#DCF3EB] p-[3px] rounded">
                  <span class="k-user text-green-500 text-[20px]" />
                </div>
                <span
                  class="text-[16px] text-[#626262]"
                >Total User Berlangganan</span>
                <img
                  v-b-tooltip.hover.topRight="
                    'Jumlah dari total registrasi user KomAds'
                  "
                  src="https://storage.googleapis.com/komerce/assets/icons/info-circle.svg"
                  alt="Info"
                  class="w-[17px] h-[17px]"
                >
              </div>
              <div class="w-[120px]">
                <FlatPickr
                  v-model="selectedPeriod"
                  :config="datePickerConfig"
                  class="custom-select"
                />
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="text-black text-[38px] font-[600]">{{ subscriptionOverview.total_user_subscribe }}</span>
            </div>
            <span class="text-[#626262]">Bulan ini : {{ subscriptionOverview.total_user_subscribe_current_month }}</span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 py-2 px-2 bg-[#E5E5E587]">
          <!-- Platform Cards -->
          <div
            v-for="platform in supportedPlatforms"
            :key="platform"
          >
            <PlatformCard
              :platform="platform"
              :is-active="platformSubscriptionStatus[platform]"
              @active-billing="handleActiveBilling"
              @setup-billing="handleSetupBilling"
            />
          </div>
        </div>
      </div>
    </BCard>

    <!-- Setup Billing Modal -->
    <ModalSetupBilling
      ref="setupBillingModal"
      :subscription-info="subscriptionInfoDetail"
      @submitted="handleConfirmationBilling"
    />

    <!-- Confirmation Modal (Setup / Turn Off Billing) -->
    <ModalConfirmBilling
      :mode="confirmationModalMode || 'setup'"
      :platform="confirmationModalPlatform || 'meta'"
      :processing="isConfirmationProcessing"
      @confirm-billing="handleBillingConfirmation"
    />
  </BCard>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import {
  LABELMONTH, MONTH_YEAR, START_DATE_OF_MONTH,
} from '@/libs/filterDate'
import FlatPickr from 'vue-flatpickr-component'
import { Indonesian } from 'flatpickr/dist/l10n/id'
import MonthSelect from 'flatpickr/dist/plugins/monthSelect'
import '@/@core/scss/vue/libs/vue-flatpicker.scss'
import '@core/scss/vue/libs/vue-select.scss'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import PlatformCard from './PlatformCard.vue'
import ModalSetupBilling from './ModalSetupBilling.vue'
import ModalConfirmBilling from './ModalConfirmBilling.vue'

/**
 * @typedef {'meta'|'google'|'tiktok'} PlatformType Platform iklan yang didukung
 * @typedef {'setup'|'turn_off'} ConfirmationMode Mode konfirmasi modal
 * @typedef {{ durations: number[], cost: Record<number, number> }} BillingSetupPayload Data setup billing
 */

export default {
  name: 'SetupBillingPage',

  components: {
    FlatPickr,
    PlatformCard,
    ModalSetupBilling,
    ModalConfirmBilling,
  },

  /**
   * Component data initialization
   * @returns {Object} Component data state
   */
  data() {
    return {
      // Date selection
      selectedPeriod: START_DATE_OF_MONTH(new Date()),
      monthLabels: LABELMONTH,

      // Subscription data state
      subscriptionOverview: {},
      subscriptionInfoDetail: null,

      // Loading states
      loading: {
        subscriptionInfo: false,
      },

      // Date picker configuration
      datePickerConfig: {
        mode: 'single',
        locale: Indonesian,
        altInput: true,
        altFormat: 'j/n/Y',
        dateFormat: 'Y-m-d',
        maxDate: new Date(),
        plugins: [
          new MonthSelect({
            shorthand: true,
            dateFormat: 'Y-m-d',
            altFormat: 'M Y',
          }),
        ],
      },

      // Platform subscription status
      platformSubscriptionStatus: {
        meta: false,
        google: false,
        tiktok: false,
      },

      // Modal state management
      pendingToggleAction: null,

      // Confirmation modal state
      confirmationModalMode: null,
      confirmationModalPlatform: null,
      confirmationModalPayload: null,
      isConfirmationProcessing: false,
    }
  },

  computed: {
    /**
     * Supported advertising platforms
     * @returns {PlatformType[]}
     */
    supportedPlatforms() {
      return ['meta', 'google', 'tiktok']
    },
  },

  watch: {
    /**
     * Watch for period changes and refresh subscription data
     */
    selectedPeriod: {
      handler: 'fetchSubscriptionOverview',
      immediate: false,
    },
  },

  /**
   * Lifecycle hook - component mounted
   */
  mounted() {
    this.fetchSubscriptionOverview()
  },

  methods: {
    /**
     * Fetch subscription overview data for selected period
     * @returns {Promise<void>}
     */
    async fetchSubscriptionOverview() {
      this.loading.subscriptionInfo = true

      const month = MONTH_YEAR(this.selectedPeriod)
      const url = '/komads/api/v1/admin/subscription_info'

      await newAxiosIns.get(url, { params: { month } }).then(response => {
        const { data } = response.data
        this.subscriptionOverview = data
        this.updatePlatformSubscriptionStatus(data)
      }).finally(() => {
        this.loading.subscriptionInfo = false
      })
    },

    /**
     * Update platform subscription status from API response
     * @param {Object} subscriptionData - Subscription data from API
     */
    updatePlatformSubscriptionStatus(subscriptionData) {
      Object.keys(subscriptionData)
        .filter(key => key.includes('_subscription_status'))
        .forEach(key => {
          const platform = key.replace('_subscription_status', '')
          if (Object.prototype.hasOwnProperty.call(this.platformSubscriptionStatus, platform)) {
            this.platformSubscriptionStatus[platform] = subscriptionData[key]
          }
        })
    },

    /**
     * Handle active billing modal opening from PlatformCard
     * @param {{ platform_name: PlatformType, intendedActive?: boolean }} context - Modal opening payload
     */
    handleActiveBilling(context) {
      if (context && Object.prototype.hasOwnProperty.call(context, 'intendedActive')) {
        const { platform_name, intendedActive } = context
        this.pendingToggleAction = context

        if (!intendedActive) {
          this.prepareDeactivationConfirmation(platform_name)
          return
        }
      } else {
        this.pendingToggleAction = null
      }

      // Open setup billing modal
      this.$bvModal.show('modal-setup-billing')
    },

    /**
     * Handle setup billing modal close
     * @param {{ context: { platform_name: PlatformType } }} context - Modal closing payload
     */
    handleSetupBilling(context) {
      this.getSubscriptionInfoDetail(context)

      this.pendingToggleAction = context

      this.$bvModal.show('modal-setup-billing')
    },

    /**
     * Prepare deactivation confirmation modal
     * @param {PlatformType} platform - Platform to deactivate
     */
    prepareDeactivationConfirmation(platform) {
      this.confirmationModalMode = 'turn_off'
      this.confirmationModalPlatform = platform
      this.isConfirmationProcessing = false

      this.$nextTick(() => {
        this.$bvModal.show('modal-confirm-billing')
      })
    },

    /**
     * Handle confirmation billing modal submission
     * @param {BillingSetupPayload} payload - Billing setup data
     */
    handleConfirmationBilling(payload) {
      this.confirmationModalMode = 'setup'
      this.confirmationModalPlatform = this.pendingToggleAction.platform_name
      this.confirmationModalPayload = payload
      this.isConfirmationProcessing = false

      this.$bvModal.hide('modal-setup-billing')

      this.$nextTick(() => {
        this.$bvModal.show('modal-confirm-billing')
      })
    },

    async getSubscriptionInfoDetail(payload) {
      // eslint-disable-next-line camelcase
      const { platform_name } = payload
      const url = '/komads/api/v1/admin/subscription_info/detail'
      await newAxiosIns.get(url, { params: { platform_name } }).then(response => {
        const { data } = response.data
        this.subscriptionInfoDetail = data
      })
    },

    /**
     * Activate/deactivate platform subscription
     * @param {{ platform_name: PlatformType, subscription_status: boolean }} payload - Activation payload
     * @returns {Promise<void>}
     */
    async activatePlatformSubscription(payload) {
      // eslint-disable-next-line camelcase
      const { platform_name, subscription_status } = payload
      const url = '/komads/api/v1/admin/subscription_info/activation'

      await newAxiosIns.post(url, {
        platform_name,
        subscription_status,
      })
    },

    /**
     * Setup platform billing configuration
     * @param {{ platform_name: PlatformType, subscription_data: BillingSetupPayload }} payload - Billing setup payload
     * @returns {Promise<void>}
     */
    async configurePlatformBilling(payload) {
      // eslint-disable-next-line camelcase
      const { platform_name, subscription_data } = payload
      const url = '/komads/api/v1/admin/subscription_info/update'

      await newAxiosIns.post(url, {
        platform_name,
        subscription_data,
      })
    },

    /**
     * Execute billing confirmation action
     * @param {{ mode: ConfirmationMode, platform_name: PlatformType }} context - Confirmation context
     * @returns {Promise<void>}
     */
    async handleBillingConfirmation(context) {
      const { mode, platform_name } = context || {}
      this.isConfirmationProcessing = true

      try {
        if (mode === 'setup') {
          await Promise.all([
            this.activatePlatformSubscription({
              platform_name,
              subscription_status: true,
            }),
            this.configurePlatformBilling({
              platform_name,
              ...this.confirmationModalPayload,
            }),
          ])
        } else {
          await this.activatePlatformSubscription({
            platform_name,
            subscription_status: false,
          })
        }

        this.handleBillingSuccess(mode)
      } catch (error) {
        this.handleBillingError(mode)
      } finally {
        this.isConfirmationProcessing = false
      }
    },

    /**
     * Handle successful billing operation
     * @param {ConfirmationMode} mode - Operation mode
     */
    handleBillingSuccess(mode) {
      const message = {
        setup: 'Berhasil mengubah biaya langganan',
        turn_off: 'Berhasil menonaktifkan langganan',
      }

      this.$toast_success({ message: message[mode] })

      // Reset modal state
      this.$refs.setupBillingModal.resetState()

      // Clear pending toggle for setup operations
      if (mode === 'setup') {
        this.pendingToggleAction = null
      }

      // Refresh subscription data
      this.fetchSubscriptionOverview()

      // Close confirmation modal
      this.$bvModal.hide('modal-confirm-billing')
    },

    /**
     * Handle billing operation error
     * @param {ConfirmationMode} mode - Operation mode
     */
    handleBillingError(mode) {
      const message = {
        setup: 'Gagal mengubah biaya langganan',
        turn_off: 'Gagal menonaktifkan langganan',
      }

      this.$toast_error({ message: message[mode] })
    },
  },
}
</script>

<style scoped>
.subscription-overview {
  background:
    url('https://storage.googleapis.com/komerce/komads-meta/bg-komads.svg')
    no-repeat right top 40px,
    #fdfdfd;
  background-size: 550px;
}

/* Responsive design improvements */
@media (max-width: 768px) {
  .subscription-overview {
    background-size: 400px;
    background-position: right top 20px;
  }
}

@media (max-width: 576px) {
  .subscription-overview {
    background-size: 300px;
    background-position: right top 10px;
  }
}
</style>
